import React from 'react';
import { Popper, PopperChildrenProps } from 'react-popper';
import type { Modifier, Options } from '@popperjs/core/lib';
import Portal from '@ids-ts/portal';
import { Alignments, Positions } from './types';
import { getPlacementKey, placementMap } from './getPlacement';
import { getOffset } from './getOffset';

type Modifiers = Partial<Modifier<string, object>>[];

export interface PositionProps {
  /**
   * vertical alignments: top, bottom, middle
   * horizontal alignments: left, right, center
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'left'
   */
  alignment?: Alignments;
  /** The element would be rendered in correct position to show. */
  children?: React.ReactNode;
  /** Optional className applied to root node. */
  className?: string;
  /**
   * componentName is a required prefix with rendered position to generate a position className,
   * if componentName is not given, then no position className been added.
   * Position className would like: componentName-topPosition-leftAlignment
   */
  componentName?: string;
  /** Optional id to be passed to the portal */
  id?: string;
  /**
   * offsetSkidding: the cross-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  offsetSkidding?: number;
  /**
   * offsetDistance: the main-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  offsetDistance?: number;
  /**
   * offsetX: the x axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   * @deprecated Please use offsetSkidding instead
   */
  offsetX?: number;
  /**
   * offsetY: the y axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   * @deprecated Please use offsetDistance instead
   */
  offsetY?: number;
  /**
   * vertical positions: top, bottom
   * horizontal positions: left, right
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'top'
   */
  position?: Positions;
  /**
   * preventOverflow: used to prevent the popper from being positioned outside the boundary.
   * options:
   * mainAxis: boolean, // true
   * altAxis: boolean, // false
   * padding: number | Partial<SideObject>, // 0
   * boundary: HTMLElement | HTMLElement[] | "clippingParents", // "clippingParents"
   * altBoundary: boolean, // false
   * rootBoundary: "viewport" | "document", // "viewport"
   * tether: boolean, // true
   * tetherOffset: (({ popper: Rect, reference: Rect, placement: Placement }) => number) | number, // 0
   * More detail in popper.js modifiers: preventOverflow API document.
   */
  preventOverflow?: Options;
  /** Whether to show the rendered children */
  show?: boolean;
  /** The HTML DOM element used for target. */
  targetElement?: Element | HTMLElement | null;
  /** Optional style applied to root node. */
  style?: React.CSSProperties;
}

/**
 * Position an element on the page
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const Position = ({
  alignment = 'left',
  children,
  className,
  componentName,
  id,
  offsetSkidding = 0,
  offsetDistance = 0,
  offsetX = 0,
  offsetY = 0,
  position = 'top',
  preventOverflow,
  show,
  style: optionalStyle,
  targetElement
}: PositionProps) => {
  /** render children element */
  const renderChildrenElement = ({
    placement,
    ref,
    style
  }: PopperChildrenProps) => {
    const [tPosition, tAlignment] = (placementMap[placement] || '').split('-');
    const positionClassName =
      !componentName || !tPosition || !tAlignment
        ? ''
        : `${componentName}-${tPosition}Position-${tAlignment}Alignment`;

    const cloneChildren = React.cloneElement(children as React.ReactElement, {
      placement: placementMap[placement],
      className: `${
        (children as React.ReactElement).props.className || ''
      } ${positionClassName}`.trim(),
      'data-placement': placementMap[placement]
    });

    return (
      <div
        ref={ref}
        style={{ ...optionalStyle, ...style }}
        className={className}
      >
        {cloneChildren}
      </div>
    );
  };

  const placementKey = getPlacementKey(alignment, position);
  const offset = getOffset({
    placement: placementKey,
    offsetSkidding,
    offsetDistance,
    offsetX,
    offsetY
  });

  const modifiers: Modifiers = [
    {
      name: 'offset',
      options: {
        offset
      }
    }
  ];

  if (preventOverflow) {
    modifiers.push({
      name: 'preventOverflow',
      options: preventOverflow
    });
  }

  return show && targetElement ? (
    <Portal
      ownerDocument={(targetElement as HTMLElement).ownerDocument}
      id={id}
    >
      <Popper
        referenceElement={targetElement}
        placement={placementKey}
        modifiers={modifiers}
      >
        {renderChildrenElement}
      </Popper>
    </Portal>
  ) : null;
};

export default Position;
